import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDatasetClassesOptions } from "hooks/useDatasetClassesOptions";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import {
  getExportRequestData,
  getLabelPropagationFeatureOptions,
} from "redux/SingleDataset/selectors";
import { useSendSeedAssignment } from "hooks/LabelPropagation/useSendSeedAssignment";
import { useDeleteSeedAssignment } from "./useDeleteSeedAssignment";
import { CardItem } from "views/components/LabelPropagation/SelectedClassesListStep/ClassCard";
import { SeedStepRef } from "views/components/LabelPropagation/SeedStep";
import { useGetSeed } from "./useGetSeed";
import {
  FLOW_PARAMETER,
  SEED_PARAMETER,
} from "helpers/constants/miscellaneous";
import { useLocation } from "react-router";
import { useGetStats } from "./useGetStats";
import { useStepsConfig } from "./useStepsConfig";
import { VLActionModalProps } from "views/uikit/VLModal/VLActionModal";
import { FlywheelStatus } from "../../vl-api/vlSchemas";

export const useLabelPropagation = (
  datasetId: string,
  onLabelPropagationCompleted: () => void,
  resetShouldFetch: () => void,
  showSummaryTab: () => void,
  flywheelStatus?: FlywheelStatus,
  shouldFetch?: string,
) => {
  const [selectedValues, setSelectedValues] = useState<string[]>();
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [classItemsMap, setClassItemsMap] = useState<
    Record<string, CardItem[]>
  >({});

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const seedId = searchParams.get(SEED_PARAMETER);
  const initialFlowId = searchParams.get(FLOW_PARAMETER);
  const [modalProps, setModalProps] = useState<VLActionModalProps | null>(null);

  const { fetchSeed, seed, loading: isSeedLoading } = useGetSeed();

  const [step, setStep] = useState<number>(initialFlowId ? 2 : 1);

  // Ref to avoid infinite fetchSeed loop
  const hasFetchedSeedRef = useRef(false);
  const seedStepRef = useRef<SeedStepRef>(null);

  // Fetch seed once
  useEffect(() => {
    if (!seedId || hasFetchedSeedRef.current) return;

    let isMounted = true;

    const fetchSeedOnce = async () => {
      try {
        await fetchSeed(datasetId, seedId);
        if (isMounted) hasFetchedSeedRef.current = true;
      } catch (err) {
        console.error(err);
      }
    };

    fetchSeedOnce();

    return () => {
      isMounted = false;
    };
  }, [datasetId, seedId, fetchSeed]);

  // Map seed to classItemsMap
  useEffect(() => {
    if (!seed) return;

    const map: Record<string, CardItem[]> = {};
    seed.forEach((cluster) => {
      map[cluster.labelCategoryId] = cluster.items.map((item) => ({
        id: item.id,
        imgUri: item.imgUri,
        originUri: item.originUri,
      }));
    });

    setClassItemsMap(map);
    setSelectedValues(seed.map((cluster) => cluster.labelCategoryId));
  }, [seed]);

  // Dataset classes
  const {
    data: classesOptions,
    loading: classesLoading,
    error: classesError,
  } = useDatasetClassesOptions(datasetId);

  const queryParamsFilters = useAllQueryParamsFilters();
  const exportRequestData = useSelector((state) =>
    getExportRequestData(state, queryParamsFilters, true),
  );

  const requiredSeedSamplesPerLabel = useSelector((state) =>
    getLabelPropagationFeatureOptions(state),
  ).required_seed_samples_per_label;

  const { sendSeedAssignment } = useSendSeedAssignment();

  const allClassesMeetLimit = useCallback(() => {
    if (!selectedValues?.length) return false;
    return selectedValues.every((className) => {
      const items = classItemsMap?.[className];
      if (!items || !Array.isArray(items)) return false;
      return items.length >= requiredSeedSamplesPerLabel;
    });
  }, [selectedValues, classItemsMap, requiredSeedSamplesPerLabel]);

  const assignItemsToClassHandler = useCallback(
    async (className: string) => {
      if (!className || !seedId) return;

      setSelectedCard(className);
      try {
        const items = await sendSeedAssignment(
          className,
          exportRequestData,
          datasetId,
          seedId,
        );
        if (items) {
          const itemsMap: Record<string, CardItem[]> = {};
          items.forEach((item) => {
            itemsMap[item.labelCategoryId] = item.items;
          });
          setClassItemsMap(itemsMap);
        }
      } finally {
        setSelectedCard(null);
      }
    },
    [sendSeedAssignment, exportRequestData, datasetId, seedId],
  );

  const { deleteAssignment, deleteSingleItemFromAssignment } =
    useDeleteSeedAssignment();

  const onDelete = useCallback(
    async (labelCategoryId: string) => {
      if (!seedId) return;
      try {
        await deleteAssignment({ datasetId, seedId, labelCategoryId });
        setSelectedValues((prev) => prev?.filter((v) => v !== labelCategoryId));
        setClassItemsMap((prev) =>
          Object.fromEntries(
            Object.entries(prev).filter(([key]) => key !== labelCategoryId),
          ),
        );
      } catch (e) {
        console.error("Failed to delete assignment", e);
      }
    },
    [datasetId, seedId, deleteAssignment],
  );

  const onSingleItemDelete = useCallback(
    async (labelCategoryId: string, mediaId: string) => {
      if (!seedId) return;
      try {
        await deleteSingleItemFromAssignment({
          datasetId,
          seedId,
          labelCategoryId,
          mediaId,
        });

        setClassItemsMap((prev) => {
          const currentItems = prev[labelCategoryId] || [];
          const updatedItems = currentItems.filter(
            (item) => item.id !== mediaId,
          );
          return { ...prev, [labelCategoryId]: updatedItems };
        });
      } catch (e) {
        console.error("Failed to delete assignment", e);
      }
    },
    [datasetId, seedId, deleteSingleItemFromAssignment],
  );

  const { loading: statsLoading } = useGetStats();
  const { loading: seedLoading } = useGetSeed();

  const stepsConfiguration = useStepsConfig({
    selectedValues,
    setSelectedValues,
    options: classesOptions,
    selectedCard,
    progressLimit: requiredSeedSamplesPerLabel,
    loading: classesLoading,
    error: classesError,
    allClassesMeetLimit: allClassesMeetLimit(),
    onDelete,
    onItemDelete: onSingleItemDelete,
    classItemsMap,
    step,
    setStep,
    datasetId,
    requiredSeedSamplesPerLabel,
    setModal: setModalProps,
    seedStepRef,
    setClassItemsMap,
    shouldFetch,
    resetShouldFetch,
    onLabelPropagationCompleted,
    flywheelStatus,
    showSummaryTab,
  });

  const currentStep =
    stepsConfiguration.find((s) => s.key === step) || stepsConfiguration[0];

  const isLoading =
    classesLoading || isSeedLoading || statsLoading || seedLoading;

  return {
    seedId,
    loading: false,
    currentStep,
    step,
    stepsCount: stepsConfiguration.length,
    selectedLabels: selectedValues,
    classesOptions,
    assignItemsToClassHandler,
    isLoading,
    modal: modalProps,
  };
};
